import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import userStoreModule from '../userStoreModule'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const tableColumns = computed(() => {
    const columns = [
      { key: 'user', sortable: true },
      { key: 'role', sortable: true },
      { key: 'timeInRole', sortable: true },
      { key: 'experience', label: 'Exp.', sortable: false },
      { key: 'level', label: 'Level', sortable: true },
      { key: 'location', sortable: true },
      { key: 'advisor', label: 'CGF', sortable: true },
      { key: 'actions' },
    ]
    if (archivedFilter.value === 'false') {
      columns.splice(7, 0, { key: 'isActive', label: 'Active', sortable: true })
    }
    return columns
  })

  const limit = ref(10)
  const totalUsers = ref(0)
  const page = ref(1)
  const perPageOptions = [5, 10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('updatedAt')
  const sortOrder = ref('desc')
  const roleFilter = ref(null)
  const levelFilter = ref(null)
  const statusFilter = ref(null)
  const archivedFilter = ref('false');

  const isNominationModalActive = ref(false)
  const nominations = ref([{ user: null, engagement: '' }])

  const addNominationRow = () => {
    nominations.value.push({ user: null, engagement: '' })
  }

  const removeNominationRow = (index) => {
    nominations.value.splice(index, 1)
  }

  const saveNominations = () => {
    // Logic to save nominations
    isNominationModalActive.value = false
  }

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: limit.value * (page.value - 1) + (localItemsCount ? 1 : 0),
      to: limit.value * (page.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch([page, limit, searchQuery, roleFilter, levelFilter, statusFilter, archivedFilter], () => {
    refetchData()
  })

  const fetchUsers = (ctx, callback) => {
    store
      .dispatch('app-user/fetchUsers', {
        q: searchQuery.value,
        limit: limit.value,
        page: page.value,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
        role: roleFilter.value,
        level: levelFilter.value,
        isActive: statusFilter.value === 'active' ? true : statusFilter.value === 'inactive' ? false : null,
        showArchived: archivedFilter.value,
      })
      .then(response => {
        const { users, total } = response.data
        // Removing the PRS Admin user 
        const filteredUsers = users.filter((u) => { return (u.level.name.toLowerCase() != 'prs admin') })
        callback(filteredUsers)
        totalUsers.value = total - 1
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'user') return 'secondary'
    if (role === 'admin') return 'warning'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  // Experience helpers
  // Returns 'New to Role' or 'Seasoned' based on category and seniority
  const resolveExperienceLabel = (level, roleStartDate) => {
    /*
    Associates: <1 year is new in role, otherwise seasoned
    Senior Associates: <1.5 years is new in role, otherwise seasoned
    Managers: <1.5 years is new in role, otherwise seasoned
    Senior Managers: <2 years is new in role, otherwise seasoned
    Partners: <3 years is new in role, otherwise seasoned
    Seniority is determined by level.name containing "senior"
    */
    if (!level || !level.category || !level.name || !roleStartDate) return 'N/A'
    const category = String(level.category).toLowerCase()
    const isSenior = String(level.name).toLowerCase().includes('senior')
    let threshold = null

    if (category === 'associates') {
      threshold = isSenior ? 1.5 : 1
    } else if (category === 'managers') {
      threshold = isSenior ? 2 : 1.5
    } else if (category === 'partners') {
      threshold = !String(level.name).toLowerCase().includes('associate') ? 3 : 2
    }

    if (!threshold) return 'N/A'
    const start = new Date(roleStartDate)
    const now = new Date()
    const diffYears = (now - start) / (1000 * 60 * 60 * 24 * 365)
    return diffYears < threshold ? 'New to Role' : 'Seasoned'
  }

  // Returns badge variant based on experience
  const resolveExperienceVariant = (level, roleStartDate) => {
    if (!level || !level.category || !level.name || !roleStartDate) return 'secondary'
    const category = String(level.category).toLowerCase()
    const isSenior = String(level.name).toLowerCase().includes('senior')
    let threshold = null

    if (category === 'associates') {
      threshold = isSenior ? 1.5 : 1
    } else if (category === 'managers') {
      threshold = isSenior ? 2 : 1.5
    } else if (category === 'partners') {
      threshold = !String(level.name).toLowerCase().includes('associate') ? 3 : 2
    }

    if (!threshold) return 'secondary'
    const start = new Date(roleStartDate)
    const now = new Date()
    const diffYears = (now - start) / (1000 * 60 * 60 * 24 * 365)
    return diffYears < threshold ? 'light-info' : 'light-success'
  }

  return {
    fetchUsers,
    tableColumns,
    limit,
    page,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    sortOrder,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    resolveExperienceLabel,
    resolveExperienceVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    levelFilter,
    statusFilter,
    archivedFilter,

    isNominationModalActive,
    nominations,
    addNominationRow,
    removeNominationRow,
    saveNominations,
  }
}
